import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import { createJobWithBridge } from '../../services/cutlist-bridge';
import { ArrowLeft, Home, FileText, AppWindow, Lightbulb, AlertTriangle, Loader2, Save } from 'lucide-react';
import { noAutofill } from '../../utils/noAutofill';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const FIELD_STYLE = {
  border: '1px solid rgba(139,69,19,0.15)',
  borderRadius: '8px',
  padding: '10px 14px',
  fontSize: '13px',
  color: INK,
  fontFamily: FONT_SANS,
  outline: 'none',
  background: PAPER,
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

function Field({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <label style={{ fontSize: '10.5px', fontWeight: '700', color: WALNUT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}{required && <span style={{ color: RUST, marginLeft: '3px' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function SectionHeader({ Icon, title, subtitle }) {
  return (
    <div style={{ padding: '14px 24px', background: WALNUT, display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Icon size={19} strokeWidth={2} color="white" />
      <div>
        <div style={{ color: 'white', fontWeight: '700', fontSize: '15px' }}>{title}</div>
        <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px' }}>{subtitle}</div>
      </div>
    </div>
  );
}

export default function NewCutlistJob() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    customerName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    stateZip: '',
    woNumber: '',
    jobName: '',
    locationTown: '',
    soldDate: (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
    jobType: 'install',
    windowCount: '',
    doorCount: '',
    durationDays: '1',
    comments: '',
  });

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function focusStyle(e) { e.target.style.borderColor = WALNUT; }
  function blurStyle(e) { e.target.style.borderColor = 'rgba(139,69,19,0.15)'; }

  async function handleSave() {
    if (!form.customerName.trim()) { setError('Customer name is required.'); return; }
    if (!form.woNumber.trim()) { setError('WO / Contract Number is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const { cutlistJobId } = await createJobWithBridge(form);
      navigate('/cutlist/' + cutlistJobId + '/cutlist');
    } catch (e) {
      console.error(e);
      setError('Failed to create job. Check your connection.');
      setSaving(false);
    }
  }

  const breadcrumb = 'Dashboard › Cutlist Dashboard › New Job';

  return (
    <AppLayout title="New Job" breadcrumb={breadcrumb}>
      <div style={{ maxWidth: '1000px', width: '100%', margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: FONT_SANS }}>

        <button
          onClick={() => navigate('/cutlist')}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: WALNUT, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, padding: 0, alignSelf: 'flex-start' }}
        >
          <ArrowLeft size={14} strokeWidth={2} /> Back to Cutlist Dashboard
        </button>

        {/* CUSTOMER SECTION */}
        <div style={{ background: PAPER, borderRadius: '14px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', overflow: 'hidden' }}>
          <SectionHeader Icon={Home} title="Customer Information" subtitle="Contact details for this job" />
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
              <Field label="Customer Name" required>
                <input type="text" name="twp-newjob-customer" {...noAutofill} value={form.customerName} onChange={e => update('customerName', e.target.value)} placeholder="Full name..." style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Phone">
                <input type="tel" name="twp-newjob-phone" {...noAutofill} value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(605) 000-0000" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Email">
                <input type="email" name="twp-newjob-email" {...noAutofill} value={form.email} onChange={e => update('email', e.target.value)} placeholder="customer@email.com" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr', gap: '16px' }}>
              <Field label="Street Address">
                <input type="text" name="twp-newjob-address" {...noAutofill} value={form.address} onChange={e => update('address', e.target.value)} placeholder="123 Main St..." style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="City">
                <input type="text" name="twp-newjob-city" {...noAutofill} value={form.city} onChange={e => update('city', e.target.value)} placeholder="Milbank" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="State / Zip">
                <input type="text" name="twp-newjob-statezip" {...noAutofill} value={form.stateZip} onChange={e => update('stateZip', e.target.value)} placeholder="SD 57252" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>
          </div>
        </div>

        {/* JOB SECTION */}
        <div style={{ background: PAPER, borderRadius: '14px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', overflow: 'hidden' }}>
          <SectionHeader Icon={FileText} title="Job Details" subtitle="WO number, location, type, and dates" />
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <Field label="WO / Contract Number" required>
                <input type="text" name="twp-newjob-wo" {...noAutofill} value={form.woNumber} onChange={e => update('woNumber', e.target.value)} placeholder="WO-0000" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Job / Project Name">
                <input type="text" name="twp-newjob-name" {...noAutofill} value={form.jobName} onChange={e => update('jobName', e.target.value)} placeholder="Kitchen remodel..." style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Location / Town" required>
                <input type="text" name="twp-newjob-town" {...noAutofill} value={form.locationTown} onChange={e => update('locationTown', e.target.value)} placeholder="Milbank, Watertown..." style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Field label="Sold Date">
                <input type="date" name="twp-newjob-solddate" {...noAutofill} value={form.soldDate} onChange={e => update('soldDate', e.target.value)} style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Job Type">
                <select value={form.jobType} onChange={e => update('jobType', e.target.value)} style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="install">Install</option>
                  <option value="repair">Repair</option>
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pickup at Shop</option>
                </select>
              </Field>
            </div>
            <Field label="Notes / Comments">
              <textarea name="twp-newjob-comments" {...noAutofill} value={form.comments} onChange={e => update('comments', e.target.value)} placeholder="Any notes about this job..." rows={3} style={{ ...FIELD_STYLE, resize: 'vertical', lineHeight: '1.5' }} onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
          </div>
        </div>

        {/* SCOPE SECTION */}
        <div style={{ background: PAPER, borderRadius: '14px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', overflow: 'hidden' }}>
          <SectionHeader Icon={AppWindow} title="Job Scope" subtitle="Approximate counts for scheduling" />
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <Field label="Window Count">
                <input type="number" min="0" value={form.windowCount} onChange={e => update('windowCount', e.target.value)} placeholder="0" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Door Count">
                <input type="number" min="0" value={form.doorCount} onChange={e => update('doorCount', e.target.value)} placeholder="0" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Estimated Duration (days)">
                <input type="number" min="1" max="30" value={form.durationDays} onChange={e => update('durationDays', e.target.value)} placeholder="1" style={FIELD_STYLE} onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>
          </div>
        </div>

        {/* Info banner */}
        <div style={{ background: 'rgba(139,69,19,0.04)', border: '1px solid rgba(139,69,19,0.1)', borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <Lightbulb size={16} strokeWidth={2} color={WALNUT} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '12px', color: STONE, lineHeight: '1.5' }}>
            <strong style={{ color: WALNUT }}>One save — two records.</strong> Saving creates the job in the scheduler marked as <strong>Sold</strong> and opens a cut list record ready for window entry. Location/Town appears in all customer notifications and the scheduler dashboard.
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: `${RUST}14`, border: `1px solid ${RUST}33`, borderRadius: '8px', padding: '12px 16px', color: RUST, fontSize: '13px' }}>
            <AlertTriangle size={15} strokeWidth={2} /> {error}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', borderRadius: '10px', border: 'none', background: saving ? '#ccc' : WALNUT, color: 'white', fontSize: '15px', fontWeight: '700', fontFamily: FONT_SANS, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: saving ? 'none' : '0 4px 12px rgba(139,69,19,0.25)' }}>
            {saving ? <><Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> Saving...</> : <><Save size={16} strokeWidth={2} /> Save Job & Start Cut List</>}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </AppLayout>
  );
}
